/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Else, If, Then } from "react-if";
import { Box } from "@nordcloud/gnui";
import { SortOrder, ApplicationsSortFields } from "~/generated/graphql";
import { TableToolbar, TableToolbarSearch } from "~/components";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import {
  minSearchLength,
  TableSearchInput,
  getNoSearchResultsMessage,
} from "~/components/SearchInput";
import { SortSelector } from "~/components/SortSelector";
import { useQueryState } from "~/hooks";
import { isEmpty } from "~/tools";
import { useGetApplications } from "~/views/applications/hooks/useGetApplications/useGetApplications";
import { ApplicationQuery } from "../types";
import { ApplicationItem } from "./ApplicationItem";
import { UnallocatedResources } from "./UnallocatedResources";

const sortOptions = [
  {
    value: ApplicationsSortFields.Name,
    label: "Application Name",
  },
];

export function ApplicationTable() {
  const { queryState, updateQueryState } = useQueryState<ApplicationQuery>();
  const { count, data, loading } = useGetApplications({
    page: queryState.page,
    limit: queryState.limit,
    sort: {
      field: queryState.field ?? ApplicationsSortFields.Name,
      order: queryState.order ?? SortOrder.Asc,
    },
    filter: {
      phrase: queryState.searchPhrase,
    },
  });

  const handleSortDirection = (order: SortOrder) => {
    updateQueryState({
      ...queryState,
      order,
    });
  };

  const handleSortField = (field: ApplicationsSortFields) => {
    updateQueryState({
      ...queryState,
      field,
    });
  };

  const handlePhraseField = (field: string | undefined) => {
    updateQueryState({
      ...queryState,
      searchPhrase: field,
    });
  };

  const isSearchAllowed =
    !queryState.searchPhrase ||
    queryState.searchPhrase.length >= minSearchLength;

  return (
    <>
      <Box boxStyle="lightGrey" spacing="spacing04">
        <TableToolbar>
          <SortSelector<ApplicationsSortFields>
            options={sortOptions}
            value={queryState.field ?? ApplicationsSortFields.Name}
            direction={queryState.order ?? SortOrder.Asc}
            onSelect={handleSortField}
            onSortChange={handleSortDirection}
          />
          <TableToolbarSearch>
            <TableSearchInput<ApplicationQuery>
              onSubmit={handlePhraseField}
              placeholder="Type a Keyword (e.g. Application Name, Environment Name, etc.)"
            />
          </TableToolbarSearch>
        </TableToolbar>
      </Box>
      <UnallocatedResources />
      <If condition={!isSearchAllowed}>
        <Then>
          <NoData hasIcon message={getNoSearchResultsMessage()} />
        </Then>
        <Else>
          <LoaderWrap loading={loading} inContent>
            <If condition={isEmpty(data)}>
              <Then>
                <NoData hasIcon message={"There are no Applications."} />
              </Then>
              <Else>
                <>
                  {data.map((application) => {
                    return (
                      <ApplicationItem
                        key={application.id}
                        application={application}
                      />
                    );
                  })}
                  <Pagination count={count} />
                </>
              </Else>
            </If>
          </LoaderWrap>
        </Else>
      </If>
    </>
  );
}
